import { useNavigate, useParams } from "react-router-dom";
import { changeMode, changeModeForField, fieldTruePermission, prepareDefaultValues, prepareInitialConfig } from "../../../../utils/formHelper";
import { useEffect, useMemo, useState } from "react";
import ToggleField from "../../../Components/Inputs/ToggleButton";
import { useForm, Controller } from "react-hook-form";
import { Box, MenuItem, Select, FormHelperText } from "@mui/material";
import { getApi, postApi } from "../../../../utils/services";
import { ADD_AUTOMATED_NOTIFICATION, GET_AUTOMATED_NOTIFICATION_BY_CLIENTID } from "../../../../utils/services/apiPath";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { TabGridAndFormLayout } from "@proscore-ai/proscore-ui-library";

const remainderOptionConfig = {
  timesheetPayrollRemainder: {
    label: "Timesheet/Payroll Reminders",
    withInfoIcon: false,
    perKey: "timesheetPayrollRemainder",
    defaultValue: false
  },
  certifiedPayroll: {
    label: "Certified Payroll (CPR)",
    withInfoIcon: false,
    perKey: "certifiedPayroll",
    defaultValue: false
  }
};

const reminderDays = Array.from({ length: 8 }, (_, i) => i);

const ClientRemainder = () => {
  const { id } = useParams();
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(remainderOptionConfig, "read"));
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);
  const [automatedNotificationData, setAutomatedNotificationData] = useState({});

  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  const onEdit = () => setFormConfig(changeMode("edit"));

  const resetForm = () => {
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "read" } }), {}));
  };

  const onCancel = () => {
    resetForm();
  };

  const onSave = async data => {
    const payload = {
      clientId: id,
      timesheetPayrollReminder: {
        isEnabled: data.timesheetPayrollRemainder,
        reminderDayAfter: data.timesheetReminderDays
      },
      cprReminder: {
        isEnabled: data.certifiedPayroll,
        reminderDayAfter: data.certifiedPayrollDays
      }
    };
    const { error } = await postApi(ADD_AUTOMATED_NOTIFICATION, payload);
    if (error) return errorToast(error);
    getSelectedAutomatedNotification();
    resetForm();
    successToast(`Client Reminder Updated Successfully`);
  };

  const { handleSubmit, control, watch, reset} = useForm({
    defaultValues: {
      ...prepareDefaultValues(remainderOptionConfig),
      timesheetReminderDays: 0,
      certifiedPayrollDays: 0
    }
  });

 const [initialFormValues, setInitialFormValues] = useState(null);

const getSelectedAutomatedNotification = async () => {
  try {
    const data = await getApi(`${GET_AUTOMATED_NOTIFICATION_BY_CLIENTID}/${id}`);

    const patchedValues = {
      timesheetPayrollRemainder: data.timesheetPayrollReminder?.isEnabled ?? false,
      timesheetReminderDays: data.timesheetPayrollReminder?.reminderDayAfter ?? 0,
      certifiedPayroll: data.cprReminder?.isEnabled ?? false,
      certifiedPayrollDays: data.cprReminder?.reminderDayAfter ?? 0
    };

    reset(patchedValues);
    setInitialFormValues(patchedValues);
    setAutomatedNotificationData(data);
  } catch (error) {
    errorToast(error);
  }
};

useEffect(() => {
  getSelectedAutomatedNotification();
}, [id]);

const watchedValues = watch();

useEffect(() => {
  if (!initialFormValues) return;

  const hasChanged = Object.keys(initialFormValues).some(
    key => watchedValues[key] !== initialFormValues[key]
  );

  if (hasChanged) {
    setFormConfig(changeMode("edit")); 
  }
}, [watchedValues, initialFormValues]);

  const defaultFormProps = { control,formValues: automatedNotificationData, handleReadValueClick };

  const timesheetEnabled = watch("timesheetPayrollRemainder");
  const certifiedEnabled = watch("certifiedPayroll");

  return (
    <TabGridAndFormLayout
      showGrid={false}
      title={"Reminders"}
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      canView={true}
      hideEdit={false}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
    >
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", rowGap: 3 }}>
        <Box>
          <ToggleField {...defaultFormProps} {...formConfig.timesheetPayrollRemainder} permission={fieldTruePermission} />
          {timesheetEnabled && (
            <Controller
              name="timesheetReminderDays"
              control={control}
              render={({ field }) => (
                <Box sx={{ mt: 1, gap: 2, display: "flex", alignItems: "center" }}>
                  <FormHelperText>Send Reminder (days after pay period end date)</FormHelperText>
                  <Select {...field} size="small">
                    {reminderDays.map(day => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            />
          )}
        </Box>
        <Box>
          <ToggleField {...defaultFormProps} {...formConfig.certifiedPayroll} permission={fieldTruePermission} />
          {certifiedEnabled && (
            <Controller
              name="certifiedPayrollDays"
              control={control}
              render={({ field }) => (
                <Box sx={{ mt: 1, gap: 2, display: "flex", alignItems: "center" }}>
                  <FormHelperText>Send Reminder (days after pay period end date)</FormHelperText>
                  <Select {...field} size="small">
                    {reminderDays.map(day => (
                      <MenuItem key={day} value={day}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            />
          )}
        </Box>
      </Box>
    </TabGridAndFormLayout>
  );
};

export default ClientRemainder;
